import { useState, useEffect, useLayoutEffect, useContext, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import cx from "classnames";
import _ from "lodash";
import Portal from "components/Portal";
import { isMacOs } from 'react-device-detect';
import { OverlayTrigger, Popover, Toast } from "react-bootstrap";
import { EditorContext } from "components/Editor";
import StoryboardContext from "components/Storyboard/context";
import { StretchSectionDialog } from "components/Editor/widgets/section";
import { StretchCarouselDialog } from "components/Editor/widgets/carousel";
import ManageMenu from "components/Editor/widgets/menu/manageMenu";
import { StretchDialog } from "components/Editor/widgets/seek";
import { StretchMenuDialog } from "components/Editor/widgets/menu";
import Actions from "../actions";
import { ImageAltEditor } from "components/Editor/widgets/image";
import { useDispatch, useSelector } from "react-redux";
import { isWidgetLocked, useContainerSelector, useSelectedWidgetSelector, useWidgetChildrenSelector } from "components/Editor/hooks/useEditorSession";
import { actionGroup, alignWidgets, arrangeWidget, cloneWidgets, clonePages, createWidget, deleteWidgets, recalculateSectionColumnWidths, selectWidget, toggleEditMode, toggleHotspotView, unselectWidget, updateWidgetActions, updateWidgetLayout, updateWidgetProperty } from "actions/editor";
import { ASSET, BASIC_TEXT, BUTTON, CAROUSEL, CAROUSEL_CARD, CONTENT, CONTENT_CARD, CONTENT_FILTER_CONTAINER, CONTENT_PAGINATION_BUTTON, CONTENT_SEARCH, FORM, FORM_CHECK_BOX, FORM_CHECK_LIST, FORM_DROPDOWN_LIST, FORM_FIELD, FORM_NUMBER, FORM_STEP, FORM_SUBMIT, FORM_TEXTAREA, GROUP, HOTSPOT, HTML, IFRAME, IMAGE, LINE, LIVE_ELEMENT, LIVE_ELEMENT_CONTAINER, MENU, MENU_CONTAINER, MENU_DRAGGABLE_CONTAINER, OTP_FORM_SUBMIT, OTP_INPUT, OTP_RESEND, PAGE, RECTANGLE, SECTION, SECTION_COLUMN, SEEK, SHAPE, SLIDE, TEXT, VIDEO } from "constants/widgetTypes";
import { createGroupWidget, unGroupWidget } from "actions/group";
import { WidgetRegistry } from "components/Editor/widgets/registry";
import { useEditableWidget, useEditableWidgets } from "../tools";
import { getWidgetType } from "components/Editor/helper/widget";
import { getBoundingRectForWidget, getBoundingRectFromLayout } from "components/Editor/helper/layoutManager";
import { PAGE_CONTAIN_STORYBOARDS, SLIDE_STORYBOARDS, supportsFrameEditorFeatures } from "constants/storyboardType";
import styles from "./menu.module.scss";

const ignoreHide = [SECTION_COLUMN, CONTENT_CARD, CONTENT_PAGINATION_BUTTON, BASIC_TEXT];
const ALLOWED_GROUP = [TEXT, BUTTON, SHAPE, LINE, IMAGE, VIDEO, HTML, IFRAME, GROUP, RECTANGLE];
const actionTypes = [TEXT, BUTTON, IMAGE, SHAPE, RECTANGLE, GROUP];
const animationTypes = [TEXT, BUTTON, HOTSPOT, IMAGE, VIDEO, SHAPE, RECTANGLE, BASIC_TEXT, LINE, GROUP];

export default function Menu({positionX, positionY, setShowMenu}) {
    const dispatch = useDispatch();
    const editorContext = useContext(EditorContext);
    const storyboardContext = useContext(StoryboardContext);
    const { id: pageId } = useParams();

    const editorSession = editorContext.editorSession.id;
    const storyboardType = _.get(storyboardContext, 'type');
    const entityType = useSelector(state => _.get(state, `${editorSession}.present.entityType`));
    const supportsFrames = supportsFrameEditorFeatures(entityType, storyboardType);
    const selectedPage = useSelector(state => _.get(state, `${editorSession}.present.selectedPage`, null));
    const activeFrameId = useSelector(state => {
      const activeSegment = _.get(state, `${editorSession}.present.activeSegment`, null);
      return selectedPage !== activeSegment ? activeSegment : selectedPage;
    });
    const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
    const selection = useSelector(state => _.get(state, `${editorSession}.present.selected`));
    const allWidgets = useSelector(state =>_.get(state, `${editorSession}.present.widgets`));
    const selectedWidgets = useSelectedWidgetSelector(editorSession);
    const firstSelectedWidget = selectedWidgets.length > 0 ? selectedWidgets[0] : null;
    const isDesktopBreakpoint = breakpoint === "desktop";
    const isSingleSelection = selectedWidgets.length === 1;
    const container = useContainerSelector(editorSession, selectedWidgets[0]?.uid);
    const locked = _.get(firstSelectedWidget, `properties.${breakpoint}.locked`);
    const role = _.get(storyboardContext, 'role', null);
    const liveElement = useSelector(state => _.get(state, `liveElements.liveElement.${pageId}.item`));
    const userId = useSelector((state) => _.get(state, 'users.me.id'));
    const isLiveElementEditable = _.get(liveElement, 'createdBy.id') === userId;
    const paletteComponent = useSelector(state => _.get(state, `brandKit.components.paletteComponent.${pageId}.item`));
    const isBrandElementEditable = _.get(paletteComponent, 'createdBy.id') === userId;
    const allowAnimation = selectedWidgets?.length > 0 && selectedWidgets.every(widget =>
      animationTypes.includes(widget.type)
    );
    const editMode = useSelector(state => {
      return _.get(state, `${editorSession}.present.editMode`) === selectedWidgets[0]?.uid;
  });
    const [updatedPosition, setUpdatedPosition] = useState({ positionX: positionX, positionY: positionY });
    const [state, setState] = useState({
        cut: false,
        copy: false,
        paste: true,
        pasteAsPlain: false,
        duplicate: false,
        hide: false,
        deletable: false,
        group: false,
        ungroup: false,
        copyAnimation: false,
        pasteAnimation: false
    });
    const [dialogState, setDialogState] = useState({
      showAction: false,
      showAltText: false,
      showStretch: false,
      showManageMenu: false,
      showCarouselStretch: false,
      showMenuStretch: false,
      showSeekStretch: false,
    });

  const isSomeWidgetLocked = selectedWidgets.some(widget =>
    isWidgetLocked(allWidgets, widget, breakpoint)
  );

  const isAllWidgetLocked = selectedWidgets.every(widget =>
    isWidgetLocked(allWidgets, widget, breakpoint)
  );

    useEffect(() => {
        _calculateToolState();
    }, [selection, selectedPage, isSomeWidgetLocked]);

    useLayoutEffect(() => {
      if (positionX && positionY){
        const menuContainer = document.getElementById("menu");
        
        if (menuContainer){
          const rect = menuContainer.getBoundingClientRect();
          if (rect){
            const totalHeight =  rect.height + positionY ;
            const totalWidth =  rect.width + positionX ;
            setUpdatedPosition( {
               positionX : totalWidth >= window.innerWidth ? positionX - ((totalWidth - window.innerWidth) + 20) : positionX,
               positionY : totalHeight >= window.innerHeight ? positionY - ((totalHeight - window.innerHeight) + 20) : positionY
            });
          }
        }
      }
    }, [positionX, positionY]);

    const isParentLocked = useSelector(state => {
      if (!firstSelectedWidget) return false;
  
      const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
  
      // recursive function
      const checkLocked = (w) => {
        if (!w) return false;
  
        const parent = widgets.find(i => i.uid === w.parent);
  
        if(_.get(parent, `properties.${breakpoint}.locked`, false)) {
          return true;
        }
  
        return checkLocked(parent);
      };
  
      return checkLocked(firstSelectedWidget);
    });

    const _calculateToolState = () => {
        const pageWidgets = allWidgets.filter(widget => widget.type === PAGE);
        var cut = _.size(selection) > 0;
        var copy = _.size(selection) > 0;
        var paste = true;
        var pasteAsPlain = false;
        var duplicate = _.size(selection)  > 0;
        var hide = _.size(selection) > 0;
        var deletable = _.size(selection) > 0;
        var group = _.size(selection) > 1;
        var ungroup = _.size(selection) === 1 && selectedWidgets.some(w => w.type === GROUP);

        if (firstSelectedWidget && firstSelectedWidget.type === TEXT) {
          const isEditing = editorContext.isTextEditing[firstSelectedWidget.uid];
          pasteAsPlain = isEditing === true;
        }

        selectedWidgets.forEach(widget => {
          const sectionUid = widget.type === SECTION_COLUMN ? widget.parent : null;
          const columnCount = sectionUid ? allWidgets.filter((w) => w.parent === sectionUid).length : 0;
          const parentWidgetType = getWidgetType(editorSession, widget?.parent);
          if (columnCount === 1){
            deletable = false;
          }

          if (ignoreHide.includes(widget.type) || (parentWidgetType && parentWidgetType === FORM_STEP)){
            hide = false;
          }

          const template = _.get(widget, 'template');
    
          if(template) {
            cut = false;
            copy = false;
            duplicate = false;
            deletable = false;
          }
    
          const moveableConfig = WidgetRegistry.moveableConfig(editorSession, widget);
          const isSegmentPage = widget.type === PAGE && supportsFrames;
          cut = cut && (isSegmentPage || (_.get(moveableConfig, 'cloneable', true) && !_.includes([SECTION_COLUMN, HOTSPOT], widget.type)));
          copy = copy && (isSegmentPage || (_.get(moveableConfig, 'cloneable', true) && !_.includes([SECTION_COLUMN, HOTSPOT], widget.type)));
          duplicate = duplicate && (isSegmentPage || _.get(moveableConfig, 'cloneable', true));
          deletable = deletable && (isSegmentPage
            ? _.size(pageWidgets) >= 2 && (!_.get(widget, 'properties.desktop.visible', true) || pageWidgets.filter(p => _.get(p, 'properties.desktop.visible', true)).length >= 2)
            : _.get(moveableConfig, 'deletable', true));
        });

        if (selectedPage && _.size(selection) === 0 && supportsFrames) {
          cut = _.size(pageWidgets) > 1;
          copy = true;
          duplicate = true;
          hide = false;
          const currentPage = pageWidgets.find(p => p.uid === selectedPage);
          const visiblePageCount = pageWidgets.filter(p => _.get(p, 'properties.desktop.visible', true)).length;
          deletable = _.size(pageWidgets) >= 2 && currentPage && (!_.get(currentPage, 'properties.desktop.visible', true) || visiblePageCount >= 2);
        }

        const selectedPageCount = selectedWidgets.filter(widget => widget.type === PAGE).length;
        const allFramesSelected =
          supportsFrames &&
          selectedPageCount > 0 &&
          selectedPageCount === pageWidgets.length;

        if (allFramesSelected) {
          cut = false;
          deletable = false;
        }

        if (isSomeWidgetLocked) {
          cut = false;
          hide = false;
          group = false;
          ungroup = false;
        }

        if (isAllWidgetLocked) {
          deletable = false;
        }
    
        // --- Animation Properties ---
        const clipboardData = window.localStorage.getItem('CLIPBOARD_ANIMATION');
        const clipboardAnimation = clipboardData ? JSON.parse(clipboardData) : null;
        const clipboardType = _.get(clipboardAnimation, `animation.type`, '');
        const hasClipboardAnimation = Boolean(clipboardAnimation?.animation);
        const isParallax = clipboardType === 'parallax';
        const isSpin = clipboardType === 'spin';
        
        const isMultiSelect = selectedWidgets.length > 1;
        const hasSelection = selectedWidgets.length > 0;
        
        // --- copyAnimation ---
        const copyAnimation =
          selectedWidgets.length === 1 && // only for single select
          !isSomeWidgetLocked && // none of the selected widgets are locked
          allowAnimation && // all selected widgets are allowed for animation
          Boolean(_.get(selectedWidgets[0], `properties.${breakpoint}.animation.type`)); // the selected widget has animation
        
        // --- pasteAnimation ---
        const pasteAnimation =
          hasClipboardAnimation && // there is animation in clipboard
          hasSelection && // there is selection
          !isSomeWidgetLocked && // none of the selected widgets are locked
          allowAnimation && // all selected widgets are allowed for animation
          selectedWidgets.every(widget => {
            const isHotspot = widget.type === HOTSPOT;
        
            const disallowHotspotAnimations = isHotspot && (isParallax || isSpin); // hotspots cannot have parallax or spin animations
            const disallowMultiWidgetParallax = isMultiSelect && isParallax; // parallax cannot be pasted on multi-select
            const disallowSpatialParallax = SLIDE_STORYBOARDS.includes(storyboardType) && isParallax; // parallax cannot be pasted in spatial storyboards
        
            return (
              !disallowHotspotAnimations &&
              !disallowMultiWidgetParallax &&
              !disallowSpatialParallax
            );
          });

        setState({
          cut: cut,
          copy: copy,
          paste: paste,
          pasteAsPlain: pasteAsPlain,
          duplicate: duplicate,
          hide: hide,
          deletable: deletable,
          group: group,
          ungroup: ungroup,
          copyAnimation: copyAnimation,
          pasteAnimation: pasteAnimation
        });
    }
    
    const handleCut = () => {
        const pageWidgets = allWidgets.filter(widget => widget.type === PAGE);
        const selectedPageCount = selectedWidgets.filter(widget => widget.type === PAGE).length;
        const allFramesSelected =
          supportsFrames &&
          selectedPageCount > 0 &&
          selectedPageCount === pageWidgets.length;

        if (allFramesSelected) {
          setShowMenu(null);
          return;
        }

        editorContext.editorSession.eventBus.emit('cut');
        setShowMenu(null);
    }

    const handlePaste = async (event) => {
      const clipboardData = event.clipboardData || event.originalEvent?.clipboardData;
    
      const files = [];
      let hasFiles = false;
    
      try {
        const items = clipboardData?.items;
        if (items) {
          for (const item of items) {
            if (item.kind === 'file') {
              const file = item.getAsFile();
    
              if (file && !file.type.startsWith('text/')) {
                files.push(file);
                hasFiles = true;
              }
            }
          }
        }
    
        if (hasFiles) {
          editorContext.editorSession.eventBus.emit('pasteMedia', { files });
          setShowMenu(null);
          return;
        }
      } catch (err) {
        console.warn("Error reading clipboardData items:", err);
      }
    
      try {
        const clipboardItems = await navigator.clipboard.read();
    
        for (const item of clipboardItems) {
          for (const type of item.types) {
            if (!type.startsWith('text/')) {
              const blob = await item.getType(type);
              const ext = type.split('/')[1] || 'bin';
    
              const file = new File([blob], `pasted-file.${ext}`, { type });
              files.push(file);
              hasFiles = true;
            }
          }
        }
    
        if (hasFiles) {
          editorContext.editorSession.eventBus.emit('pasteMedia', { files });
          setShowMenu(null);
          return;
        }
      } catch (clipboardError) {
        console.log("Clipboard API access failed:", clipboardError);
      }
    
      if (firstSelectedWidget?.type === TEXT) {
        const editor = editorContext.editorRegistry[firstSelectedWidget.uid];
        if (editor) {
          const isEditing = editorContext.isTextEditing[firstSelectedWidget.uid];
    
          if (isEditing) {
            try {
              const text = await navigator.clipboard.readText();
              const pasteEvent = new Event("paste", { bubbles: true, cancelable: true });
              pasteEvent.clipboardData = {
                getData: (type) => (type === "text/plain" ? text : ""),
                types: ["text/plain"]
              };
              editor.getBody().dispatchEvent(pasteEvent);
              setShowMenu(null);
              return;
            } catch (err) {
              console.error("Clipboard paste failed:", err);
            }
          }
        }
      }
      editorContext.editorSession.eventBus.emit("paste");
      setShowMenu(null);
    };

    const handlePasteAsPlainText = async () => {
      if (firstSelectedWidget && firstSelectedWidget.type === TEXT) {
        const editor = editorContext.editorRegistry[firstSelectedWidget.uid];
        if (!editor) return;
    
        const isEditing = editorContext.isTextEditing[firstSelectedWidget.uid];
        
        if (isEditing) {
          try {
            const text = await navigator.clipboard.readText();
            
            editor.undoManager.transact(() => {
              editor.formatter.remove('bold');
              editor.formatter.remove('italic');
              editor.formatter.remove('underline');
              editor.formatter.remove('strikethrough');
              editor.formatter.remove('superscript');
              editor.formatter.remove('subscript');
              
              editor.execCommand('mceRemoveTextcolor', 'forecolor');
              editor.execCommand('mceRemoveTextcolor', 'hilitecolor');
              
              editor.execCommand('RemoveFormat');
              
              editor.execCommand('mceInsertContent', false, text);
            });
            
            setShowMenu(null);
            return;
          } catch (err) {
            console.error("Clipboard paste as plain text failed:", err);
          }
        }
      }
      setShowMenu(null);
    };

    const handleCopy = () => {
        editorContext.editorSession.eventBus.emit('copy');
        setShowMenu(null);
    }

    const handleDuplicate = () => {
      let selectedPages = supportsFrames
        ? selectedWidgets.filter(widget => widget.type === PAGE)
        : [];

      if (selectedPages.length === 0 && selectedWidgets.length === 0 && selectedPage && supportsFrames) {
        selectedPages = allWidgets.filter(
          widget => widget.type === PAGE && widget.uid === selectedPage
        );
      }

      if (selectedPages.length > 0 && activeFrameId) {
        dispatch(clonePages(
          editorSession,
          selectedPages.map(page => page.uid),
          activeFrameId,
          'segment'
        ));
      }

      const widgetsByParent = selectedWidgets
        .filter(widget => widget.type !== PAGE)
        .reduce((acc, widget) => {
          const { parent, uid } = widget;

          if (!parent) {
            return acc;
          }
          
          if (!acc[parent]) {
            acc[parent] = [];
          }
          acc[parent].push(uid);

          return acc;
      }, {});

      Object.entries(widgetsByParent).forEach(([parent, widgetIds]) => {
        dispatch(cloneWidgets(editorSession, parent, widgetIds));
      });

      setShowMenu(null);
  }

    const handleHide = () => {
        selectedWidgets.forEach(widget => {
            let id = widget.uid;
            const visible = _.get(widget, `properties.${breakpoint}.visible`);
            dispatch(updateWidgetProperty(editorSession, id, breakpoint, {
                visible: !visible
            }));
            if (visible) {
                dispatch(unselectWidget(editorSession, id));
            }
        });
        setShowMenu(null);
    }

    const handleDelete = () => {
      const pageWidgets = allWidgets.filter(widget => widget.type === PAGE);
      const selectedPageCount = selectedWidgets.filter(widget => widget.type === PAGE).length;
      const allFramesSelected =
        supportsFrames &&
        selectedPageCount > 0 &&
        selectedPageCount === pageWidgets.length;

      if (allFramesSelected) {
        setShowMenu(null);
        return;
      }

      if (selectedPage && _.size(selection) === 0 && supportsFrames) {
        const currentPage = pageWidgets.find(p => p.uid === selectedPage);
        const visiblePageCount = pageWidgets.filter(p => _.get(p, 'properties.desktop.visible', true)).length;
        if (
          _.size(pageWidgets) >= 2 &&
          currentPage &&
          (!_.get(currentPage, 'properties.desktop.visible', true) || visiblePageCount >= 2)
        ) {
          dispatch(deleteWidgets(editorSession, [selectedPage]));
        }
        setShowMenu(null);
        return;
      }

      const widgets = selectedWidgets
      .filter(widget => !isWidgetLocked(allWidgets, widget, breakpoint))
      .map(widget => widget.uid);

      dispatch(deleteWidgets(editorSession, widgets));
      setShowMenu(null);
    }

    const canGroupWidgets = useMemo(() => {
      return isSelectionAllowed(selectedWidgets, allWidgets);
    }, [selectedWidgets, allWidgets]);

    const handleLock = () => {
      if (firstSelectedWidget){
           if (isParentLocked) {
            return;
           }
            dispatch(updateWidgetProperty(editorSession, firstSelectedWidget.uid, breakpoint, {
              locked: !locked
            }));

            if (!locked) {
              dispatch(selectWidget(editorSession, [firstSelectedWidget.uid]));
            } else {
              dispatch(unselectWidget(editorSession, firstSelectedWidget.uid));
            }
            setShowMenu(null);
      }
    }

    const handleGroup = () => {
      if (isSelectionAllowed(selectedWidgets, allWidgets || [])) {
        dispatch(createGroupWidget(editorSession, selectedWidgets));
      }
      setShowMenu(null);
    };

    const handleUngroup = () => {
      selectedWidgets
        .filter(widget => widget.type === GROUP)
        .forEach(widget => {
          dispatch(unGroupWidget(editorSession, widget.uid));
        });
      setShowMenu(null);
    };
    

    const handleHelp = () => {
        window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank');
        setShowMenu(null);
    }

    const handleCopyAnimation = () => {
      editorContext.editorSession.eventBus.emit('copyAnimation');
      setShowMenu(null);
    };

    const handlePasteAnimation = () => {
      editorContext.editorSession.eventBus.emit('pasteAnimation');
      setShowMenu(null);
    };

    const updateDialogState = useCallback((key) => {
      setDialogState(prev => {
        const newState = Object.keys(prev).reduce((acc, curr) => {
          acc[curr] = false;
          return acc;
        }, {});
    
        newState[key] = !prev[key];
    
        return newState;
      });
    }, []);

    const closePopup = () => {
        dispatch(toggleEditMode(editorSession, selectedWidgets[0].uid, false));
        setShowMenu(null);
    }

  return (
    <Portal>
      <div className={styles.menu} id="menu" style={{ transform: `translate(${updatedPosition.positionX}px, ${updatedPosition.positionY}px)` }} >
        <MenuAction
          action={{name: "Cut", shortCut: "X"}}
          disabled={!state.cut}
          onClick={handleCut}
        />
        <MenuAction
          action={{name: "Paste", shortCut: "V"}}
          disabled={!state.paste}
          onClick={handlePaste}
        />
        <MenuAction
          action={{name: "Paste As Plain Text", shortCut: "Shift V"}}
          disabled={!state.pasteAsPlain}
          onClick={handlePasteAsPlainText}
        />
        <MenuAction
          action={{name: "Copy", shortCut: "C"}}
          disabled={!state.copy}
          onClick={handleCopy}
        />
        <MenuAction
          action={{name: "Duplicate", shortCut: "D"}}
          disabled={!state.duplicate}
          line={true}
          onClick={handleDuplicate}
        />
        <MenuAction
          action={{name: "Hide"}}
          disabled={!state.hide}
          onClick={handleHide}
        />
        <MenuAction
          action={{name: "Delete"}}
          disabled={!state.deletable}
          line={true}
          onClick={handleDelete}
        />

        {!isSomeWidgetLocked && allowAnimation &&
          <>
            <MenuAction
              action={{name: "Copy Animation"}}
              disabled={!state.copyAnimation}
              onClick={handleCopyAnimation}
            />
            <MenuAction
              action={{name: "Paste Animation"}}
              disabled={!state.pasteAnimation}
              line={true}
              onClick={handlePasteAnimation}
            />
          </>
        }
        {!isAllWidgetLocked && <ArrangeMenu setShowMenu={setShowMenu} positionX={positionX}/>}
        {!isAllWidgetLocked && <AlignMenu setShowMenu={setShowMenu} storyboardType={storyboardType} positionX={positionX}/>}
        {!isAllWidgetLocked &&<DistributeEquallyMenu setShowMenu={setShowMenu}/>}
        {(role === 'owner' || isLiveElementEditable || isBrandElementEditable) && (
          isSingleSelection &&
          !isParentLocked &&
          ![HOTSPOT].includes(firstSelectedWidget.type) && (
            <MenuAction
              action={{ name: locked ? 'Unlock' : 'Lock' }}
              disabled={false}
              onClick={handleLock}
            />
          )
        )}
        {canGroupWidgets && !isSomeWidgetLocked && 
          <MenuAction
            action={{name: "Group", shortCut: "G"}}
            disabled={!state.group}
            onClick={handleGroup}
          />
        }
        {selectedWidgets.some(w => w.type === GROUP) && !isSomeWidgetLocked && (
          <MenuAction
            action={{name: "Ungroup", shortCut: "Shift G"}}
            disabled={!state.ungroup}
            line={true}
            onClick={handleUngroup}
          />
        )}
        {isSingleSelection && firstSelectedWidget.type === IMAGE && !_.get(firstSelectedWidget, 'parameter') && isDesktopBreakpoint && !isSomeWidgetLocked &&
          <MenuAction
            id={firstSelectedWidget.uid}
            action={{name: "Alt Text"}}
            disabled={false}
            onClick={() => updateDialogState('showAltText', !dialogState.showAltText)}
          />
        }
        {!editMode && dialogState.showAltText && !isSomeWidgetLocked &&
          <ImageAltEditor
            id={`menuItem-${firstSelectedWidget.uid}`}
            editor={editorSession}
            widget={firstSelectedWidget}
            onCancel={() => {
              closePopup();
            }}
          />
        }
        {isSingleSelection && actionTypes.includes(firstSelectedWidget.type) && isDesktopBreakpoint && !isSomeWidgetLocked &&
          <LinkMenu selectedWidget={firstSelectedWidget} dialogState={dialogState} updateDialogState={updateDialogState} closePopup={closePopup} editMode={editMode}/>
        }
        {isSingleSelection && firstSelectedWidget.type === SECTION && isDesktopBreakpoint && !PAGE_CONTAIN_STORYBOARDS.includes(storyboardType) && !isSomeWidgetLocked &&
          <MenuAction
            id={firstSelectedWidget.uid}
            action={{name: "Stretch"}}
            disabled={false}
            onClick={() => updateDialogState('showStretch', !dialogState.showStretch)}
          />
        }
        {!editMode && dialogState.showStretch && !isSomeWidgetLocked &&
          <StretchSectionDialog
            id={`menuItem-${firstSelectedWidget.uid}`}
            editor={editorSession}
            widget={firstSelectedWidget}
            onCancel={() => {
              closePopup();
            }}
          />
        }
        {isSingleSelection && firstSelectedWidget.type === SECTION && isDesktopBreakpoint && !isSomeWidgetLocked &&
          <AddColumnMenu selectedWidget={firstSelectedWidget} setShowMenu={setShowMenu}/> 
        }
        {isSingleSelection && firstSelectedWidget.type === CAROUSEL && isDesktopBreakpoint && !isSomeWidgetLocked &&
          <AddSlidesMenu selectedWidget={firstSelectedWidget} setShowMenu={setShowMenu} topParent={firstSelectedWidget.parent} /> 
        }
        {isSingleSelection && firstSelectedWidget.type === CAROUSEL && isDesktopBreakpoint && (_.get(container, 'type') === PAGE || _.get(container, 'type') === SLIDE) && !isSomeWidgetLocked && 
          <MenuAction
            id={firstSelectedWidget.uid}
            action={{name: "Stretch"}}
            disabled={false}
            onClick={() => updateDialogState('showCarouselStretch', !dialogState.showCarouselStretch)}
          />
        }
        {!editMode && dialogState.showCarouselStretch && !isSomeWidgetLocked &&
          <StretchCarouselDialog
            id={`menuItem-${firstSelectedWidget.uid}`}
            editor={editorSession}
            widget={firstSelectedWidget}
            onCancel={() => {
              closePopup();
            }}
            storyboardType={storyboardType}
                />
        }
        {isSingleSelection && firstSelectedWidget.type === CONTENT && isDesktopBreakpoint && !isSomeWidgetLocked && 
          <MenuAction
            action={{name: "Manage Content"}}
            disabled={firstSelectedWidget.content.mappings.items.length === 0}
            onClick={() => dispatch(toggleEditMode(editorSession, firstSelectedWidget.uid, true))}
          />
        }
        {isSingleSelection && (firstSelectedWidget.type === MENU_CONTAINER || firstSelectedWidget.type === MENU_DRAGGABLE_CONTAINER) && isDesktopBreakpoint && !isSomeWidgetLocked && 
          <MenuAction
            id={firstSelectedWidget.uid}
            action={{name: "Manage Menu"}}
            disabled={false}
            onClick={() => updateDialogState('showManageMenu', !dialogState.showManageMenu)}
          />
        }
        {!editMode && dialogState.showManageMenu && !isSomeWidgetLocked &&
          <ManageMenu
            id={firstSelectedWidget.uid}
            editor={editorSession}
            editorContext={editorContext}
            widget={firstSelectedWidget}
            onCancel={() => {
              closePopup();
            }}
          />
        }
        {isSingleSelection && firstSelectedWidget.type === SEEK && isDesktopBreakpoint && storyboardType !== "INTERACTIVE_CONTENT" && !isSomeWidgetLocked && 
          <MenuAction
            id={firstSelectedWidget.uid}
            action={{name: "Stretch"}}
            disabled={false}
            onClick={() => updateDialogState('showSeekStretch', !dialogState.showSeekStretch)}
          />
        }
        {!editMode && dialogState.showSeekStretch && !isSomeWidgetLocked &&
          <StretchDialog
            id={`menuItem-${firstSelectedWidget.uid}`}
            editor={editorSession}
            widget={firstSelectedWidget}
            onCancel={() => {
              closePopup();
            }}
          />
        }
        {isSingleSelection && firstSelectedWidget.type === MENU && isDesktopBreakpoint && !isSomeWidgetLocked && 
          <MenuAction
            id={firstSelectedWidget.uid}
            action={{name: "Stretch"}}
            disabled={false}
            onClick={() => updateDialogState('showMenuStretch', !dialogState.showMenuStretch)}
          />
        }
        {!editMode && dialogState.showMenuStretch && !isSomeWidgetLocked &&
          <StretchMenuDialog
            id={`menuItem-${firstSelectedWidget.uid}`}
            editor={editorSession}
            widget={firstSelectedWidget}
            onCancel={() => {
              closePopup();
            }}
          />
        }
        {!isSomeWidgetLocked && <MenuAction
          action={{name: "Help"}}
          disabled={false}
          onClick={handleHelp}
        />}
      </div>
    </Portal>
  );
}

const ArrangeMenu = ({setShowMenu, positionX}) => {
    const dispatch = useDispatch();
    const [showArrange, setShowArrange] = useState(false);
    const toolIgnoreTypes = [CONTENT_FILTER_CONTAINER, CONTENT_SEARCH];
    const overflowCondition = window.innerWidth <= positionX + 435 /* menu width + popover width */ ;
    
    const editorContext = useContext(EditorContext);
    
    const editorSession = editorContext.editorSession.id;
    const widget = useEditableWidget(editorSession);
    
    const buttonState = useSelector(state => {
        if (!widget) {
          return {
            back: false,
            front: false
          };
        }
    
        var widgets = _.get(state, `${editorSession}.present.widgets`) || [];
        widgets = widgets.filter(item => item.parent === widget.parent);
        widgets = _.orderBy(widgets, 'order', 'desc');
    
        const index = widgets.findIndex(item => item.uid === widget.uid);
        const total = _.size(widgets);
    
        return {
          back: index < total - 1,
          front: index > 0,
          index: index,
          total: total
        };
    });
    
    const id = _.get(widget, 'uid');

    const bringForward = () => {
        if (!buttonState.front) {
          return;
        }
    
        dispatch(arrangeWidget(editorSession, widget, buttonState.index - 1));
        setShowMenu(null);
    }

    const bringFront = () => {
        if (!buttonState.front) {
          return;
        }
    
        dispatch(arrangeWidget(editorSession, widget, 0));
        setShowMenu(null);
    }
    
    const sendBack = () => {
        if (!buttonState.back) {
          return;
        }
    
        dispatch(arrangeWidget(editorSession, widget, buttonState.total - 1));
        setShowMenu(null);
    }
    
    const sendBackward = () => {
        if (!buttonState.back) {
          return;
        }
    
        dispatch(arrangeWidget(editorSession, widget, buttonState.index + 1));
        setShowMenu(null);
    }


    const arrangePopover = (
        <Popover
          id="popover-arrange"
          className={styles.subMenuPopup}
          style={{"width": "200px"}}
          onMouseEnter={() => setShowArrange(true)}
          onMouseLeave={() => setShowArrange(false)}
        >
          <Popover.Body>
            <MenuAction
              action={{name: "Move Forward"}}
              disabled={!buttonState.front}
              onClick={bringForward}
            />
            <MenuAction
              action={{name: "Bring Front"}}
              disabled={!buttonState.front}
              onClick={bringFront} 
            />
            <MenuAction
              action={{name: "Send Back"}}
              disabled={!buttonState.back}
              onClick={sendBack}
            />
            <MenuAction
              action={{name: "Move Backward"}}
              disabled={!buttonState.back}
              onClick={sendBackward}
            />
          </Popover.Body>
        </Popover>
    );

    return (
        <OverlayTrigger
          placement= { overflowCondition ? "left-start" : "right-start" }
          show={showArrange}
          rootClose
          overlay={arrangePopover}
        >
          <button className={cx(styles.action, styles.line)} disabled={widget == null || toolIgnoreTypes.includes(widget.type)} onMouseEnter={() => setShowArrange(true)} onMouseLeave={() => setShowArrange(false)}>
            <span>Arrange</span>
            <i className={cx(`cleverstory-drop-down`, styles.arrow)}></i>
          </button> 
        </OverlayTrigger>
    )
}

const AlignMenu = ({setShowMenu, storyboardType, positionX}) => {
    const dispatch = useDispatch();
    const [showAlign, setShowAlign] = useState(false);
    
    const editorContext = useContext(EditorContext);
    const editorSession = editorContext.editorSession.id;
    
    const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
    const allWidgets = useSelector((state) => _.get(state, `${editorSession}.present.widgets`, null));
    const widgets = useEditableWidgets(editorSession);
    const overflowCondition = window.innerWidth <= positionX + 435 /* menu width + popover width */ ;
      
    const handleAlignment = (type, direction) => {
        dispatch(alignWidgets(editorSession, breakpoint, type, direction, storyboardType, allWidgets));
        setShowMenu(null);
    }
    
    const widgetsAllowOnly = [
        TEXT, BUTTON, FORM_FIELD, OTP_INPUT, FORM_NUMBER, FORM_TEXTAREA, FORM_DROPDOWN_LIST,
        FORM_CHECK_LIST, FORM_CHECK_BOX, FORM_SUBMIT, OTP_FORM_SUBMIT, OTP_RESEND, IMAGE, VIDEO, SHAPE, LINE, GROUP, 
        FORM, BASIC_TEXT, HTML, RECTANGLE, LIVE_ELEMENT, CAROUSEL, SECTION, ASSET, CONTENT, MENU, SEEK, IFRAME
    ];
    const nonHorizontalWidgets = [CAROUSEL, SECTION, ASSET, CONTENT, MENU, SEEK];
    
    const disabled = () => {
        if (_.size(widgets) < 1) {
          return true;
        }
        return widgets.some(item => !widgetsAllowOnly.includes(item.type));
    }
    
    const disableHorizontalAlign = () => {
        if (_.size(widgets) < 1) {
          return true;
        }
    
        return widgets.some(item => {
            if (item.type === ASSET && item.properties.desktop.display === 'FREE') {
                return false;
            }
    
            if (item.type === CAROUSEL && item.properties.desktop.free) {
                return false;
            }
    
            return nonHorizontalWidgets.includes(item.type);
        });
    };
    
    const disableVerticalAlign = () => {
        if (_.size(widgets) < 1) {
          return true;
        }
      
        return widgets.some(item => {
          const parentElement = document.querySelector(`#${editorSession} .editor_widget_renderer #${item?.parent}`);
          
          return parentElement && parentElement.getAttribute('data-widget-type') === LIVE_ELEMENT_CONTAINER;
        });
    };
    
    const alignPopover = (
        <Popover
          id="popover-align"
          className={styles.subMenuPopup}
          style={{"width": "130px"}}
          onMouseEnter={() => setShowAlign(true)}
          onMouseLeave={() => setShowAlign(false)}
        >
          <Popover.Body>
            <MenuAction
              action={{name: "Left"}}
              disabled={disableHorizontalAlign()}
              onClick={() => handleAlignment('vertical', 'start')}
            />
            <MenuAction
              action={{name: "Right"}}
              disabled={disableHorizontalAlign()}
              onClick={() => handleAlignment('vertical', 'end')}
            />
            <MenuAction
              action={{name: "Center"}}
              disabled={disableHorizontalAlign()}
              onClick={() => handleAlignment('vertical', 'center')}
            />
            <MenuAction
              action={{name: "Top"}}
              disabled={disableVerticalAlign()}
              onClick={() => handleAlignment('horizontal', 'start')}
            />
            <MenuAction
              action={{name: "Bottom"}}
              disabled={disableVerticalAlign()}
              onClick={() => handleAlignment('horizontal', 'end')}
            />
            <MenuAction
              action={{name: "Middle"}}
              disabled={disableVerticalAlign()}
              onClick={() => handleAlignment('horizontal', 'center')}
            />
          </Popover.Body>
        </Popover>
    );

    return (
        <OverlayTrigger
            placement= { overflowCondition ? "left-start" : "right-start" }
            show={showAlign}
            rootClose
            overlay={!disabled() ? alignPopover : <></>}
        >
            <button className={cx(styles.action, styles.line)} disabled={disabled()} onMouseEnter={() => setShowAlign(true)} onMouseLeave={() => setShowAlign(false)}>
              <span>Align</span>
              <i className={cx(`cleverstory-drop-down`, styles.arrow)}></i>
            </button> 
        </OverlayTrigger>
    )
}

const DistributeEquallyMenu = ({setShowMenu}) => {
    const dispatch = useDispatch();
    const editorContext = useContext(EditorContext);
    const editorSession = editorContext.editorSession.id;
    const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
    const widgets = useEditableWidgets(editorSession);
    const selected = useSelector(state => _.get(state, `${editorSession}.present.selected`));
    var updatedPosition = null;

    const handleDistribute = (type) => {

      const distributeSpace = calculateSpace(type);
      const selectedWidget = sortWidgetsByPosition(type);

      if (selectedWidget.length > 2 && distributeSpace ) {
      
        selectedWidget.forEach((widget, index) => {

          if (index !== 0  ) {
            var currentlayout = _.get(widget, `properties.${breakpoint}.layout`);

            const newPosition = calculateNewPosition(selectedWidget[index - 1], widget, distributeSpace, type);

            currentlayout = {
              ...currentlayout,
              transform:{
                ...currentlayout.transform,
                translate:`${newPosition}`
              }
            }
          
            dispatch(updateWidgetLayout(editorSession, widget.uid, breakpoint, currentlayout));
            updatedPosition = currentlayout;
            setShowMenu(null);
           }
        })
      }
    }

    const calculateNewPosition = (prevWidget, currentWidget, distributeSpace, type) => {

      if (!prevWidget || !currentWidget || !distributeSpace) {
        return;
      }
      let prevlayout = updatedPosition?updatedPosition:_.get(prevWidget, `properties.${breakpoint}.layout`);
      let currentlayout = _.get(currentWidget, `properties.${breakpoint}.layout`)

      const prevposition = getBoundingRectFromLayout(prevlayout)
      const currentposition = getBoundingRectFromLayout(currentlayout);

      prevlayout = getBoundingRectForWidget(editorSession,prevWidget);

      const positionX = (prevposition.x + prevlayout.width) + distributeSpace.x;
      const positionY = (prevposition.y + prevlayout.height) + distributeSpace.y

      if (type === 'horizontal') {
        return `${positionX}px,${currentposition.y}px`;
      }

      if (type === 'vertical') {
        return `${currentposition.x}px,${positionY}px`;
      }

    }

    const calculateSpace = (type) => {
      if (selected.length < 1) {
        return
      }
    
      const moveableManager = editorContext.editorSession.moveableManager.current;
      const rect = moveableManager.getRect();
      const groupWidth = _.get(rect, 'width', '');
      const groupHeight = _.get(rect, 'height', '');

      const widgetWidth = rect.children.reduce((init, obj) => { return init + obj.width }, 0);
      const widgetHeight = rect.children.reduce((init, obj) => { return init + obj.height }, 0);

      const horizontalSpace = Math.round( (groupWidth - widgetWidth) / (selected.length - 1) );
      const verticalSpace = Math.round( (groupHeight - widgetHeight) / (selected.length - 1) );

      if (groupWidth && widgetWidth && type === 'horizontal') {
        return {x : horizontalSpace,y:0}
      }

      if (groupHeight && widgetHeight && type === 'vertical') {
        return {x : 0, y : verticalSpace}
      }

    }

    const sortWidgetsByPosition = (type) => {
       if( selected.length > 1) {
         var widgetSelected = widgets.filter((widget) => {
           return selected.some((element) => {
             return element === widget.uid;
           });
          });
       
        const sortedResult = widgetSelected.sort((prev,next) => {
          var Prevlayout = _.get(prev, `properties.${breakpoint}.layout`);
          var Nextlayout = _.get(next, `properties.${breakpoint}.layout`);
          const Prevposition = getBoundingRectFromLayout(Prevlayout);
          const Nextposition = getBoundingRectFromLayout(Nextlayout);

          if(type === 'horizontal') {
            return parseFloat(Prevposition.x) - parseFloat(Nextposition.x);     
          }

          if(type === 'vertical') {
            return parseFloat(Prevposition.y) - parseFloat(Nextposition.y);     
          }

        });

        return sortedResult;  
       }
    }
    return(
        <>
        <MenuAction
          action={{name: "Distribute Horizontal"}}
          disabled={_.size(widgets) < 2}
          onClick={() => handleDistribute('horizontal')}
        />
        <MenuAction
          action={{name: "Distribute Vertical"}}
          disabled={_.size(widgets) < 2}
          onClick={() => handleDistribute('vertical')}
          line={true}
        />
        </>
    )
}

const LinkMenu = ({selectedWidget, dialogState, updateDialogState, closePopup, editMode}) => {
  const dispatch = useDispatch();
  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const selectedContent = editorContext.editorRegistry[selectedWidget.uid]?.selection?.getNode();
  const textCheck = selectedWidget.type === TEXT ? _.get(selectedWidget, "template.widget") != null : editMode;
  const hideLinkMenu = selectedWidget.type === TEXT && textCheck;
  const linkElement = getLink(editorContext.editorRegistry[selectedWidget.uid], selectedContent);
  const actionId = linkElement ? linkElement.dataset.action : null;
  const [disabled, setDisabled] = useState(editorContext.editorRegistry[selectedWidget.uid]?.selection?.isCollapsed() && actionId === null);
  const actions = useSelector((state) => {
      const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
      const widget = widgets.find((item) => item.uid === selectedWidget.uid);
  
      return _.get(widget, "actions") || [];
  });
  
  const action = actionId && actions.find((action) => action.uid === actionId);

  // Bookmark of the word-level text selection at the moment the menu opened.
  // The context menu lives in a Portal and its buttons blur the editor, so the
  // live DOM range is lost before mceInsertLink runs; without restoring this
  // bookmark the link would apply to the whole widget instead of the word.
  const bookmarkRef = useRef(null);

  useEffect(() => {
    if (selectedWidget.type !== TEXT) return;
    const editor = editorContext.editorRegistry[selectedWidget.uid];
    if (editor?.selection && !editor.selection.isCollapsed()) {
      bookmarkRef.current = editor.selection.getBookmark(2);
    }
    // capture once on mount while the word is still selected
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const editor = editorContext.editorRegistry[selectedWidget.uid];
    if (!editor) return;

    const handleSelectionChange = () => {
      setDisabled((editor.selection?.isCollapsed() && actionId === null)|| false);
    };

    editor.on("NodeChange", handleSelectionChange);

    return () => {
      editor.off("NodeChange", handleSelectionChange);
    };
  }, [editorContext, selectedWidget.uid]);

  const handleLinkAction = () => {
    if (selectedWidget.type === TEXT){
      const editor = editorContext.editorRegistry[selectedWidget.uid];
      if (linkElement) {
        editor?.selection.select(linkElement);
      }
      // refresh the bookmark against whatever range is active right now
      // (the selected word, or the existing link we just re-selected)
      if (editor?.selection && !editor.selection.isCollapsed()) {
        bookmarkRef.current = editor.selection.getBookmark(2);
      }
    }

    updateDialogState('showAction', !dialogState.showAction)
  }

  const handleChange = (value) => {
    const editor = editorContext.editorRegistry[selectedWidget.uid];

    // restore the word-level selection before mutating the DOM, otherwise the
    // link is applied to the whole widget (see bookmarkRef above)
    if (selectedWidget.type === TEXT && editor?.selection && bookmarkRef.current) {
      editor.focus();
      editor.selection.moveToBookmark(bookmarkRef.current);
    }

    const existingID = _.get(action, "uid");
    const uid = _.get(value, "uid");
    const url = _.get(value, "value") || "#";

    var updated = [...actions];
    if (existingID) {
      updated = updated.filter((item) => item.uid != existingID);
    }

    if (value) {
      updated = [...updated, value];
      dispatch(updateWidgetActions(editorSession, selectedWidget.uid, updated));

      editor?.execCommand("mceInsertLink", false, {
        href: "" + url,
        "data-action": uid,
      });
    } else {
      dispatch(updateWidgetActions(editorSession, selectedWidget.uid, updated));
      editor?.execCommand("unlink");
    }
  };

  const handleActionChange = (action) => {
    const actions = action ? [action] : [];
    dispatch(updateWidgetActions(editorSession, selectedWidget.uid, actions));
  }

  return(
    <>
    {!hideLinkMenu &&
      <>
        <MenuAction
          id={selectedWidget.uid}
          action={{name: "Link"}}
          disabled={selectedWidget.type === TEXT ? disabled : false}
          onClick={handleLinkAction}
        />
        {!textCheck && dialogState.showAction &&
          <Actions
            target={`menuItem-${selectedWidget.uid}`}
            editorSession={editorSession}
            value={selectedWidget.type === TEXT ? action : _.get(selectedWidget, 'actions.0')}
            onChange={selectedWidget.type === TEXT ? handleChange : handleActionChange}
            onCancel={() => {
              closePopup();
            }}
          />
        }
      </>
    }
    </>
  )
}

const AddColumnMenu = ({selectedWidget, setShowMenu}) => {
  const dispatch = useDispatch();
  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const columns = useWidgetChildrenSelector(editorSession, selectedWidget.uid);

  const handleAddColumns = (id) => {
    const columnCount = _.size(columns) + 1;
    const width = 100 / columnCount;

    actionGroup.start();

    dispatch(
      createWidget(
        editorSession,
        [{
          type: SECTION_COLUMN,
          name: "Column",
          properties: {
            "desktop": {
              "visible": true,
              "layout": {
                "transform": {
                  "translate": "0px,0px",
                  "rotate": "0deg"
                },
                "width": `${width}%`,
                "height": "auto"
              },
              "container": {
                "minWidth": 1200 * width / 100,
                "alignment": "center"
              },
              "alignment": null,
              "background": null
            },
            "mobile": {
              "visible": true,
              "layout": {
                "transform": {
                  "translate": "0px, 0px"
                },
                "width": "100%",
                "height": "200px"
              },
              "container": {
                "minWidth": 375,
                "alignment": null
              },
              "alignment": "center",
              "background": null
            },
          },
        }],
        id)
    );

    dispatch(recalculateSectionColumnWidths(editorSession, id));

    actionGroup.end();
    setShowMenu(null);
  }

  return (
    <>
      <MenuAction
        action={{name: "Add Column"}}
        disabled={columns.length > 5}
        onClick={() => handleAddColumns(selectedWidget.uid)}
      />
    </>
  )
}

const AddSlidesMenu = ({selectedWidget, setShowMenu, topParent}) => {
  const dispatch = useDispatch();
  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const slides = useWidgetChildrenSelector(editorSession, selectedWidget.uid);
  const parentType = getWidgetType(editorSession, topParent);
  const childrenWidgets = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`);
    return _.filter(widgets, (data) => { return data.parent === selectedWidget.uid });
})

  const handleAddSlides = (parent) => {
    const defaultChild = _.head(childrenWidgets);
    let newSlideProperties = _.get(defaultChild, 'properties');
    newSlideProperties = {
      ...newSlideProperties,
      "desktop": {
        ...newSlideProperties.desktop,
        "locked": false,
        "background": {
          "type": "color",
          "color": {
            "type": "solid",
            "r": 0,
            "g": 0,
            "b": 0,
            "a": 1
          },
          "media": null,
          "opacity": 0,
          "scale": null,
          "position": null
        },
        "container": {
          "minWidth": 1200 * 100 / 100,
          "alignment": "center"
        },
      },
      "mobile": {
        ...newSlideProperties.mobile,
        "locked": false 
      }
    }

    if (_.size(childrenWidgets) == 50) {
      return;
    }

    actionGroup.start();
    dispatch(createWidget(
      editorSession,
      [
        {
          type: CAROUSEL_CARD,
          name: `Slide`,
          properties: newSlideProperties
        },
      ],
      parent
    ))
    if (parentType !== HOTSPOT){
      dispatch(toggleHotspotView(editorSession, null));
    }
    dispatch(selectWidget(editorSession, [parent]))
    actionGroup.end();
    setShowMenu(null);
  }

  return (
    <>
    <MenuAction
       action={{name: "Add Slide"}}
       disabled={slides.length === 50}
       onClick={() => handleAddSlides(selectedWidget.uid)}
    />
    </>
  )
}

const MenuAction = ({id, action, disabled, onClick, line = false}) => {
    return(
        <button id={`menuItem-${id}`} className={cx(styles.action, {[styles.line] : line})} disabled={disabled} onClick={onClick}>
          <span>{action.name}</span>
          {action.shortCut &&
            <div className={cx(styles.shortCut, {[styles.shortCutDisabled] : disabled})}>
              {isMacOs ? <i className={cx(`cleverstory-command-icon`)}></i> : "Ctrl"}
              <span>{action.shortCut}</span>
            </div>
          }
        </button>
    )
}

//helper function
const getLink = (textEditor, node) => {
  if (!node || !textEditor) {
    return null;
  }

  if (node.tagName === "A") {
    return node;
  }

  var partialSelection =
    node.textContent != textEditor.selection?.getContent();
  if (partialSelection) {
    return;
  }

  var linkElement = textEditor.dom.getParent(node, "a[data-action]");
  if (linkElement) {
    return linkElement;
  }

  const children = Array.from(node.children);
  if (children.length == 1) {
    const firstElement = children[0];
    if (firstElement.tagName === "A") {
      return firstElement;
    }
  }

  return null;
};

function isSelectionAllowed(selectedWidgets, widgets) {
  if (!selectedWidgets || selectedWidgets.length < 2) return false;

  let parents = selectedWidgets.map(el => el.parent).filter(Boolean);
  parents = _.uniq(parents);

  if (_.size(parents) !== 1) return false;

  const parent = widgets.find(w => w.uid === parents[0]);
  if ([GROUP, CONTENT_CARD].includes(parent?.type)) return false;

  return selectedWidgets.every(widget =>
    _.includes(ALLOWED_GROUP, widget.type) &&
    !widgets.some(w => w.parent === widget.uid && w.type === HOTSPOT)
  );
}
